import { createSignal, createEffect, untrack, onMount, onCleanup, Show, For } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { Row, Column, Button } from "@lightningtv/solid-ui";
import { useNavigate } from "@solidjs/router";
import { setIsPlaying } from "../../../state";
import { ChannelCard, MovieCard, ServiceCard } from "./HomeCards";
import { useHomeRows } from "./useHomeRows";
import HotelClock from "./HotelClock";
import SettingsPanel from "../settings/SettingsPanel";
import MoviesPanel from "../movies/MoviesPanel";
import ServicesPanel from "../services/ServicesPanel";
import SmartSearchPanel from "../search/SmartSearchPanel";

type Panel = "movies" | "services" | "settings" | "search" | null;

// Home screen for hotel properties (property_data.type === "hotel"):
// the hotel background with the same top tabs as the residential home and
// the Live TV and movie rows along the bottom.
const HotelHome = () => {
  const navigate = useNavigate();
  const {
    channels, movies, services,
    isLoadingChannels, isLoadingMovies, isLoadingServices,
    movieCategoryId,
  } = useHomeRows();

  let topNavRef: any;
  let contentColumnRef: any;

  // ── Top tabs ── Movies/Services/Settings/Search open inline over the
  // screen (full-screen opaque panels), like on the residential home.
  const [openPanel, setOpenPanel] = createSignal<Panel>(null);
  const [topNavSelectedIndex, setTopNavSelectedIndex] = createSignal(0);
  const [isTopNavFocused, setIsTopNavFocused] = createSignal(false);
  const navButtonRefs: Record<string, any> = {};
  // focusEntry() handles the panels attach, so Down from the tabs lands in
  // the open panel instead of the rows hidden behind it
  const panelRefs: Record<string, { focusEntry?: () => void }> = { movies: {}, services: {}, search: {} };

  const closePanel = (panel: Exclude<Panel, null>) => {
    setOpenPanel(null);
    navButtonRefs[panel]?.setFocus?.();
  };

  const playFirstChannel = () => {
    const first = channels()[0];
    if (first?.stream_id) {
      navigate(`/livetv?num=${first.stream_id}&isTrue=true&fromHome=true`);
      setIsPlaying(true);
    }
  };

  const NAV_ITEMS: { label: string; panel?: Exclude<Panel, null>; onEnter: () => void }[] = [
    { label: "Home", onEnter: () => setOpenPanel(null) },
    { label: "Live TV", onEnter: playFirstChannel },
    { label: "Movies", panel: "movies", onEnter: () => setOpenPanel("movies") },
    { label: "Services", panel: "services", onEnter: () => setOpenPanel("services") },
    { label: "Settings", panel: "settings", onEnter: () => setOpenPanel("settings") },
  ];

  // ── Back-press-to-exit (same behaviour as the residential home) ──
  const [showExitToast, setShowExitToast] = createSignal(false);
  let exitToastTimer: ReturnType<typeof setTimeout> | null = null;

  const handleBackPress = () => {
    if (!showExitToast()) {
      setShowExitToast(true);
      exitToastTimer = setTimeout(() => {
        setShowExitToast(false);
        exitToastTimer = null;
      }, 4000);
    } else {
      if (exitToastTimer) clearTimeout(exitToastTimer);
      setShowExitToast(false);
      try { (window as any).AndroidBridge?.exitApp?.(); } catch (_) { }
    }
  };

  onCleanup(() => {
    if (exitToastTimer) clearTimeout(exitToastTimer);
  });

  // Start on the channels row. The Column's autofocus runs before anything
  // has loaded (every row is still skipFocus), so it can't land there itself;
  // once channels arrive, put focus on them — unless the user has already
  // gone to the tabs or opened a panel.
  let focusedChannelsOnce = false;
  createEffect(() => {
    if (focusedChannelsOnce || channels().length === 0) return;
    focusedChannelsOnce = true;
    if (untrack(() => openPanel() || isTopNavFocused())) return;
    if (contentColumnRef) {
      contentColumnRef.selected = 0;
      contentColumnRef.setFocus();
    }
  });

  onMount(() => {
    // Tell Android the home screen is rendering - restore WebView transparency
    try {
      (window as any).AndroidVideoPlayer?.notifyHomeReady?.();
    } catch (_) { }
  });

  return (
    <View
      color={hexColor("#000000")}
      style={{ width: 1920, height: 1080 }}
      onEscape={handleBackPress}
    >
      <View src="assets/hotel-home-bg.jpg" width={1920} height={1080} />
      {/* Lighter than hero-gradient-mask: fades the left side and the bottom
          to black, so the middle and top right of the background stay visible */}
      <View src="assets/hotel-gradient-mask.png" width={1920} height={1080} />

      {/* Time and date, top left under the background's hotel logo (drawn
          before the panels, which cover it) */}
      <HotelClock x={88} y={190} />

      {/* Rows along the bottom; Down scrolls the movies row up into view.
          Clipped so the scrolled-away row doesn't cover the top half. */}
      <View
        y={540}
        width={1920}
        height={540}
        clipping
        // Column moves Up/Down between the rows itself (and scrolls); Up on
        // the top row isn't handled there and bubbles here, to the tabs
        onUp={() => { topNavRef?.setFocus(); return true; }}
      >
        <Column
          ref={contentColumnRef}
          autofocus
          scroll="always"
          gap={40}
        >
          {/* Live TV Channels */}
          <View x={80} height={360} forwardFocus={1} skipFocus={channels().length === 0}>
            <Text skipFocus fontSize={32} color={hexColor("#FFFFFF")} style={{ fontWeight: "bold" }}>
              Live TV Channels
            </Text>
            <View y={60} forwardFocus={0}>
              <Show
                when={channels().length > 0}
                fallback={
                  <Text fontSize={20} color={hexColor("#B0B0B0")}>
                    {isLoadingChannels() ? "Loading channels..." : "No channels available"}
                  </Text>
                }
              >
                <Row gap={20} scroll="always" selected={0}>
                  <For each={channels()}>
                    {(channel) => (
                      <ChannelCard
                        channel={channel}
                        onEnter={() => {
                          if (channel.stream_id) {
                            navigate(`/livetv?num=${channel.stream_id}&isTrue=true`);
                            setIsPlaying(true);
                          }
                        }}
                      />
                    )}
                  </For>
                </Row>
              </Show>
            </View>
          </View>

          {/* Popular Movies */}
          <View x={80} height={520} forwardFocus={1} skipFocus={movies().length === 0}>
            <Text skipFocus fontSize={32} color={hexColor("#FFFFFF")} style={{ fontWeight: "bold" }}>
              Popular Movies
            </Text>
            <View y={60} forwardFocus={0}>
              <Show
                when={movies().length > 0}
                fallback={
                  <Text fontSize={20} color={hexColor("#B0B0B0")}>
                    {isLoadingMovies() ? "Loading movies..." : "No movies available"}
                  </Text>
                }
              >
                <Row gap={20} scroll="always">
                  <For each={movies()}>
                    {(movie) => (
                      <MovieCard
                        movie={movie}
                        fallbackCategoryId={movieCategoryId()}
                        onOpen={(streamId, categoryId) => navigate(`/entity/movie/${streamId}/${categoryId}`)}
                      />
                    )}
                  </For>
                </Row>
              </Show>
            </View>
          </View>

          {/* Services — OK opens the Services panel (selecting a service
              there doesn't open anything yet either) */}
          <View x={80} height={360} forwardFocus={1} skipFocus={services().length === 0}>
            <Text skipFocus fontSize={32} color={hexColor("#FFFFFF")} style={{ fontWeight: "bold" }}>
              Services
            </Text>
            <View y={60} forwardFocus={0}>
              <Show
                when={services().length > 0}
                fallback={
                  <Text fontSize={20} color={hexColor("#B0B0B0")}>
                    {isLoadingServices() ? "Loading services..." : "No services available"}
                  </Text>
                }
              >
                <Row gap={20} scroll="always">
                  <For each={services()}>
                    {(service) => (
                      <ServiceCard service={service} onEnter={() => setOpenPanel("services")} />
                    )}
                  </For>
                </Row>
              </Show>
            </View>
          </View>
        </Column>
      </View>

      {/* Panels opened from the tabs — each covers the whole screen; the
          tabs below are drawn after them so they stay on top */}
      <Show when={openPanel() === "settings"}>
        <SettingsPanel onEscape={() => closePanel("settings")} onUp={() => topNavRef?.setFocus?.()} />
      </Show>
      <Show when={openPanel() === "movies"}>
        <MoviesPanel
          onEscape={() => closePanel("movies")}
          onUp={() => topNavRef?.setFocus?.()}
          panelRef={panelRefs.movies}
        />
      </Show>
      <Show when={openPanel() === "services"}>
        <ServicesPanel
          onEscape={() => closePanel("services")}
          onUp={() => topNavRef?.setFocus?.()}
          panelRef={panelRefs.services}
        />
      </Show>
      <Show when={openPanel() === "search"}>
        <SmartSearchPanel
          onEscape={() => closePanel("search")}
          onUp={() => topNavRef?.setFocus?.()}
          panelRef={panelRefs.search}
        />
      </Show>

      {/* Top tabs — same as the residential home */}
      <View x={580} y={40}>
        <Row
          ref={topNavRef}
          gap={20}
          scroll="none"
          onSelectedChanged={(index: number) => setTopNavSelectedIndex(index)}
          onFocus={() => setIsTopNavFocused(true)}
          onBlur={() => setIsTopNavFocused(false)}
          onDown={() => {
            const panel = openPanel();
            const entry = panel && panelRefs[panel]?.focusEntry;
            if (entry) entry();
            else if (!panel) contentColumnRef?.setFocus();
            return true;
          }}
        >
          <For each={NAV_ITEMS}>
            {(item, index) => (
              <Button
                ref={(el: any) => { if (item.panel) navButtonRefs[item.panel] = el; }}
                onEnter={item.onEnter}
                style={{
                  height: 60,
                  width: 120,
                  borderRadius: 30,
                  color: !isTopNavFocused() && index() === topNavSelectedIndex()
                    ? hexColor("#3A3A3A")
                    : hexColor("#00000000"),
                  focus: {
                    color: hexColor("#ffffff"),
                    textColor: hexColor("#000000")
                  },
                }}
              >
                {item.label}
              </Button>
            )}
          </For>

          {/* Search — icon-only, so a plain View (Button wraps its children in a Text) */}
          <View
            ref={(el: any) => { navButtonRefs.search = el; }}
            onEnter={() => setOpenPanel("search")}
            style={{
              width: 60,
              height: 60,
              borderRadius: 30,
              color: hexColor("#00000000"),
              alpha: 0.6,
              border: { width: 2, color: hexColor("#ffffff") },
              focus: {
                alpha: 0.9,
                border: { width: 3, color: hexColor("#ffffff") }
              }
            }}
          >
            <View x={16} y={16} src="https://i.ibb.co/nNhBcw8M/search.png" width={28} height={28} />
          </View>
        </Row>
      </View>

      <Show when={showExitToast()}>
        <View
          x={60} y={60}
          width={500} height={100}
          color={hexColor("#000000")}
          alpha={0.9}
          borderRadius={15}
          border={{ width: 2, color: hexColor("#FFFFFF") }}
          style={{ zIndex: 9999 }}
        >
          <View x={0} y={0} width={500} height={100} color={hexColor("#FF5722")} alpha={0.2} borderRadius={15} />
          <Text x={100} y={22} fontSize={28} color={hexColor("#FFFFFF")} style={{ fontWeight: "bold", textAlign: "center" }}>
            Press Back Again to Exit
          </Text>
          <Text x={120} y={60} fontSize={18} color={hexColor("#CCCCCC")} style={{ textAlign: "center" }}>
            Press once more to close the app
          </Text>
        </View>
      </Show>
    </View>
  );
};

export default HotelHome;
